/**
 * Page-flip gesture handler for profile view
 * Provides a 3D page-turning effect on horizontal swipe with snap-back
 */

export interface PageFlipOptions {
  /** Element to apply the flip transform to */
  element: HTMLElement;
  /** Callback when flipped left (swiped left past threshold). Omit to always snap back. */
  onFlipLeft?: () => void;
  /** Callback when flipped right (swiped right past threshold). Omit to always snap back. */
  onFlipRight?: () => void;
  /** Fraction of screen width needed to complete flip (default: 0.50) */
  threshold?: number;
}

type Phase = 'idle' | 'detecting' | 'flipping' | 'animating';

const DETECT_DISTANCE = 10;
const MAX_ANGLE = 90;
const PERSPECTIVE = 1200;
const SNAP_DURATION = 250;
const COMPLETE_DURATION = 200;
const MAX_PEEL = 12;
const PEEL_FACTOR = 0.08;

export function setupPageFlipGesture(opts: PageFlipOptions): () => void {
  const { element } = opts;
  const threshold = opts.threshold ?? 0.50;

  let phase: Phase = 'idle';
  let startX = 0;
  let startY = 0;
  let currentX = 0;
  let currentY = 0;
  let direction: 'left' | 'right' | null = null;
  let reveal: HTMLDivElement | null = null;
  let currentPeel = 0;
  let thresholdCrossed = false;
  function createReveal(dir: 'left' | 'right'): HTMLDivElement {
    const el = document.createElement('div');
    const isPass = dir === 'left';
    el.className = `pub-flip-reveal ${isPass ? 'pub-flip-reveal--pass' : 'pub-flip-reveal--interested'}`;

    const label = isPass ? 'NOT FOR ME' : 'INTERESTED';
    const grid = document.createElement('div');
    grid.className = 'pub-flip-reveal-grid';
    el.appendChild(grid);

    // Insert into DOM so we can measure
    element.parentElement!.insertBefore(el, element);

    const PAD = 5;  // matches CSS padding
    const GAP = 2;  // matches CSS gap
    const availW = el.clientWidth - PAD * 2;
    const availH = el.clientHeight - PAD * 2;

    // Measure text at a known size to calculate fill font-size
    const probe = document.createElement('span');
    probe.textContent = label;
    const probePx = 16;
    probe.style.fontSize = `${probePx}px`;
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    grid.appendChild(probe);
    const textW = probe.offsetWidth;
    grid.removeChild(probe);

    const fontSize = Math.floor((availW / textW) * probePx);
    const rowH = fontSize;  // line-height: 1
    const rowCount = Math.max(1, Math.floor((availH + GAP) / (rowH + GAP)));

    grid.innerHTML = Array(rowCount)
      .fill(`<span style="font-size:${fontSize}px">${label}</span>`)
      .join('');

    return el;
  }

  function removeReveal(): void {
    reveal?.remove();
    reveal = null;
  }

  function applyTransform(angle: number, dir: 'left' | 'right', peelDeg: number): void {
    const side = dir === 'left' ? 'left' : 'right';
    // peelDeg is clamped ±12. Map to ±15% origin shift.
    const originY = 50 + peelDeg * 1.25;   // peelDeg ±12 → originY 35%–65%
    const rotateY = dir === 'left' ? -angle : angle;
    element.style.transformOrigin = `${side} ${originY}%`;
    element.style.transform = `perspective(${PERSPECTIVE}px) rotateY(${rotateY}deg)`;

    if (reveal) {
      reveal.style.opacity = String(Math.min(angle / 45, 1));
    }
  }

  function resetTransform(): void {
    element.style.transform = '';
    element.style.transformOrigin = '';
    element.classList.remove('flipping');
    removeReveal();
    currentPeel = 0;
    thresholdCrossed = false;
  }

  function animateTo(
    fromAngle: number,
    toAngle: number,
    fromPeel: number,
    dir: 'left' | 'right',
    duration: number,
    onDone: () => void
  ): void {
    phase = 'animating';
    const start = performance.now();

    function tick(now: number): void {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // circular ease-out (snappier deceleration than quad)
      const eased = Math.sqrt(1 - (1 - progress) * (1 - progress));
      const angle = fromAngle + (toAngle - fromAngle) * eased;
      const peel = fromPeel * (1 - eased); // ease peel back to 0
      applyTransform(angle, dir, peel);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        onDone();
      }
    }

    requestAnimationFrame(tick);
  }

  function finishGesture(): void {
    if (phase !== 'flipping' || !direction) {
      resetTransform();
      phase = 'idle';
      return;
    }

    const displacement = Math.abs(currentX - startX);
    const screenWidth = window.innerWidth;
    const currentAngle = Math.min((displacement / screenWidth) * MAX_ANGLE * 1.6, MAX_ANGLE);

    const callback = direction === 'left' ? opts.onFlipLeft : opts.onFlipRight;
    const pastThreshold = displacement > screenWidth * threshold && callback;

    if (pastThreshold) {
      // Animate to 90deg with peel easing to 0, then fire callback
      animateTo(currentAngle, 90, currentPeel, direction, COMPLETE_DURATION, () => {
        resetTransform();
        phase = 'idle';
        callback!();
      });
    } else {
      // Snap back to 0 with peel easing to 0
      animateTo(currentAngle, 0, currentPeel, direction, SNAP_DURATION, () => {
        resetTransform();
        phase = 'idle';
      });
    }
  }

  function onPointerMove(clientX: number, clientY: number): boolean {
    if (phase === 'detecting') {
      const dx = clientX - startX;
      const dy = clientY - startY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < DETECT_DISTANCE) return false;

      // Determine direction
      if (Math.abs(dy) > Math.abs(dx)) {
        // Vertical - bail out, let scroll happen
        phase = 'idle';
        return false;
      }

      // Horizontal - engage flip
      phase = 'flipping';
      direction = dx < 0 ? 'left' : 'right';
      element.classList.add('flipping');
      reveal = createReveal(direction);
      return true; // caller should preventDefault
    }

    if (phase === 'flipping' && direction) {
      currentX = clientX;
      currentY = clientY;
      const displacement = Math.abs(clientX - startX);
      const screenWidth = window.innerWidth;
      const angle = Math.min((displacement / screenWidth) * MAX_ANGLE * 1.6, MAX_ANGLE);

      const deltaY = currentY - startY;
      currentPeel = Math.max(-MAX_PEEL, Math.min(MAX_PEEL, -deltaY * PEEL_FACTOR));

      // Check threshold crossing for reveal feedback
      const callback = direction === 'left' ? opts.onFlipLeft : opts.onFlipRight;
      const isPastThreshold = displacement > screenWidth * threshold && !!callback;
      if (isPastThreshold && !thresholdCrossed) {
        thresholdCrossed = true;
        reveal?.classList.add('pub-flip-reveal--confirmed');
      } else if (!isPastThreshold && thresholdCrossed) {
        thresholdCrossed = false;
        reveal?.classList.remove('pub-flip-reveal--confirmed');
      }

      applyTransform(angle, direction, currentPeel);
      return true;
    }

    return false;
  }

  // --- Touch handlers ---
  function onTouchStart(e: TouchEvent): void {
    if (phase !== 'idle' || e.touches.length !== 1) return;
    phase = 'detecting';
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    currentX = startX;
    currentY = startY;
  }

  function onTouchMove(e: TouchEvent): void {
    if (phase !== 'detecting' && phase !== 'flipping') return;
    if (e.touches.length !== 1) {
      // Multi-touch: cancel
      resetTransform();
      phase = 'idle';
      return;
    }

    const shouldPrevent = onPointerMove(e.touches[0].clientX, e.touches[0].clientY);
    if (shouldPrevent) {
      e.preventDefault();
    }
  }

  function onTouchEnd(): void {
    if (phase === 'detecting') {
      phase = 'idle';
      return;
    }
    finishGesture();
  }

  function onTouchCancel(): void {
    if (phase === 'flipping') {
      animateTo(
        Math.min((Math.abs(currentX - startX) / window.innerWidth) * MAX_ANGLE * 1.6, MAX_ANGLE),
        0,
        currentPeel,
        direction || 'left',
        SNAP_DURATION,
        () => { resetTransform(); phase = 'idle'; }
      );
    } else {
      resetTransform();
      phase = 'idle';
    }
  }

  // Attach listeners (touch only - swipe feels wrong on desktop)
  element.addEventListener('touchstart', onTouchStart, { passive: true });
  element.addEventListener('touchmove', onTouchMove, { passive: false });
  element.addEventListener('touchend', onTouchEnd, { passive: true });
  element.addEventListener('touchcancel', onTouchCancel, { passive: true });

  return () => {
    element.removeEventListener('touchstart', onTouchStart);
    element.removeEventListener('touchmove', onTouchMove);
    element.removeEventListener('touchend', onTouchEnd);
    element.removeEventListener('touchcancel', onTouchCancel);
    resetTransform();
  };
}

/**
 * Programmatically trigger a page-flip animation on an element.
 * Used when the user taps a button instead of swiping.
 */
export function triggerPageFlip(
  element: HTMLElement,
  direction: 'left' | 'right',
  onDone: () => void,
): void {
  const side = direction === 'left' ? 'left' : 'right';

  // Build reveal overlay
  const isPass = direction === 'left';
  const reveal = document.createElement('div');
  reveal.className = `pub-flip-reveal ${isPass ? 'pub-flip-reveal--pass' : 'pub-flip-reveal--interested'}`;

  const label = isPass ? 'NOT FOR ME' : 'INTERESTED';
  const grid = document.createElement('div');
  grid.className = 'pub-flip-reveal-grid';
  reveal.appendChild(grid);
  element.parentElement!.insertBefore(reveal, element);

  // Measure and fill grid
  const PAD = 5;
  const GAP = 2;
  const availW = reveal.clientWidth - PAD * 2;
  const availH = reveal.clientHeight - PAD * 2;

  const probe = document.createElement('span');
  probe.textContent = label;
  const probePx = 16;
  probe.style.fontSize = `${probePx}px`;
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  grid.appendChild(probe);
  const textW = probe.offsetWidth;
  grid.removeChild(probe);

  const fontSize = Math.floor((availW / textW) * probePx);
  const rowH = fontSize;
  const rowCount = Math.max(1, Math.floor((availH + GAP) / (rowH + GAP)));
  grid.innerHTML = Array(rowCount)
    .fill(`<span style="font-size:${fontSize}px">${label}</span>`)
    .join('');

  // Start animation
  element.classList.add('flipping');
  reveal.classList.add('pub-flip-reveal--confirmed');

  const start = performance.now();

  function tick(now: number): void {
    const elapsed = now - start;
    const progress = Math.min(elapsed / COMPLETE_DURATION, 1);
    const eased = Math.sqrt(1 - (1 - progress) * (1 - progress));
    const angle = MAX_ANGLE * eased;
    const rotateY = direction === 'left' ? -angle : angle;

    element.style.transformOrigin = `${side} 50%`;
    element.style.transform = `perspective(${PERSPECTIVE}px) rotateY(${rotateY}deg)`;
    reveal.style.opacity = String(Math.min(angle / 45, 1));

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      // Cleanup and fire callback
      element.style.transform = '';
      element.style.transformOrigin = '';
      element.classList.remove('flipping');
      reveal.remove();
      onDone();
    }
  }

  requestAnimationFrame(tick);
}
